import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getAllPendingReports } from "@/data/mockData";
import {
  LayoutDashboard,
  FileText,
  CheckSquare,
  Settings,
  Menu,
  LogOut,
  Stethoscope,
  ClipboardList
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

// Mock current doctor
const currentDoctor = {
  id: "doc1",
  name: "Dr. Marco Bianchi",
  specialization: "Medicina Generale"
};

export const DoctorPortalLayout = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const pendingCount = getAllPendingReports().filter(r => r.doctorId === currentDoctor.id).length;

  const navItems = [
    { path: "/doctor", label: "Dashboard", icon: LayoutDashboard, end: true },
    { path: "/doctor/pending", label: "Da Refertare", icon: FileText, badge: pendingCount },
    { path: "/doctor/completed", label: "Refertati", icon: CheckSquare },
  ];

  const handleLogout = () => {
    navigate("/doctor/login");
  };

  const handleSwitchProfile = () => {
    navigate("/");
  };

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
          isActive
            ? "bg-primary text-primary-foreground shadow-md"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <div className="flex items-center gap-3">
        <item.icon className="h-5 w-5" />
        {item.label}
      </div>
      {item.badge > 0 && (
        <Badge variant="secondary" className="ml-2">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );

  const SidebarContent = ({ onItemClick }) => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-4">
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </div>

      {/* Doctor Info */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 border-2 border-primary/20">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {currentDoctor.name.split(" ").slice(1).map(n => n[0]).join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{currentDoctor.name}</p>
            <p className="text-xs text-muted-foreground">{currentDoctor.specialization}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} onClick={onItemClick} />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-4 space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={handleSwitchProfile}
          data-testid="doctor-switch-profile-btn"
        >
          <Stethoscope className="h-5 w-5" />
          Cambia profilo
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
        >
          <Settings className="h-5 w-5" />
          Impostazioni
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="h-5 w-5" />
          Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-card px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SidebarContent onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-primary" />
          <span className="font-semibold">Portale Medico</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-4rem)] md:min-h-screen">
          <Outlet context={{ currentDoctor }} />
        </div>
      </main>
    </div>
  );
};

export default DoctorPortalLayout;
